import { storeToRefs } from 'pinia';
import { computed, ref } from 'vue';

import type { CartsTransactionsApi } from '@/services/booking/cartsTransactionsApi';
import type { CartDetailsItemResponse, CartDetailsResponse, CartState } from '@/types/carts-transactions-api';

import { useGlobalConfigurationStore } from '@/stores/useGlobalConfigurationStore';
import { TERMINAL_CART_STATES } from '@/types/carts-transactions-api';
import { buildGA4ItemFromCartItem, pushGA4EcommerceEvent } from '@/utils/gtmEcommerce';
import { convertOctoPrice } from '@/utils/utils';

const DEFAULT_TIMEZONE = 'Europe/Paris';

export function useModeCart(
  cartsTransactionsApi: CartsTransactionsApi,
) {
  const { currentLocale } = storeToRefs(useGlobalConfigurationStore());

  const cartDetails = ref<CartDetailsResponse>();
  const deletingItemId = ref<string | null>(null);
  const deleteError = ref<{ itemId: string; message: string } | null>(null);
  const hasNoCart = ref(false);
  const isLoadingCart = ref(false);
  const terminalCartState = ref<CartState | null>(null);

  const enrichedCartItems = computed<CartDetailsItemResponse[]>(() => {
    return cartDetails.value?.items ?? [];
  });

  function isItemExpired(item: CartDetailsItemResponse): boolean {
    return item.bookingDetails?.bookingStatus === 'EXPIRED';
  }

  const hasOnlyExpiredItems = computed(() => {
    const items = enrichedCartItems.value;
    return items.length > 0 && items.every(item => isItemExpired(item));
  });

  function getEnrichedUnits(item: CartDetailsItemResponse) {
    return item.units.map((unit) => {
      const bookingUnit = item.bookingDetails?.units.find(u => u.unitId === unit.sourceUnitId);
      return {
        id: unit.unitId,
        description: bookingUnit?.name ?? unit.description ?? '',
        quantity: unit.quantity,
        originalPricePerUnit: unit.originalPrice ?? unit.retailPrice,
        netPricePerUnit: unit.retailPrice,
      };
    });
  }

  const totalPrice = computed(() => {
    if (!cartDetails.value)
      return 0;

    const totalInt = cartDetails.value.items
      .filter(item => !isItemExpired(item))
      .reduce((total, item) => {
        const itemTotal = item.units.reduce((itemSum, unit) => {
          return itemSum + unit.retailPrice * unit.quantity;
        }, 0);
        return total + itemTotal;
      }, 0);

    return convertOctoPrice(totalInt, cartDetails.value.currencyPrecision);
  });

  async function loadCartDetails() {
    isLoadingCart.value = true;
    try {
      const result = await cartsTransactionsApi.fetchCartDetails(currentLocale.value);
      if (result === null) {
        cartsTransactionsApi.clearStoredData();
        hasNoCart.value = true;
        return;
      }
      if (TERMINAL_CART_STATES.has(result.state)) {
        if (result.state === 'COMPLETED') {
          cartDetails.value = result;
        }
        cartsTransactionsApi.clearSession();
        cartsTransactionsApi.clearStoredData();
        hasNoCart.value = true;
        terminalCartState.value = result.state;
        return;
      }
      cartDetails.value = result;
      hasNoCart.value = false;
    }
    catch (err) {
      console.error('[useModeCart] Error while loading cart details:', err);
    }
    finally {
      isLoadingCart.value = false;
    }
  }

  async function removeCartItem(itemId: string) {
    deletingItemId.value = itemId;
    deleteError.value = null;

    const removedItem = cartDetails.value?.items.find(item => item.itemId === itemId);
    const cartCurrency = cartDetails.value?.currency ?? 'EUR';
    const cartCurrencyPrecision = cartDetails.value?.currencyPrecision ?? 2;

    try {
      await cartsTransactionsApi.deleteItemFromCart(itemId);
      await loadCartDetails();

      if (removedItem) {
        const ga4Items = buildGA4ItemFromCartItem(removedItem, cartCurrency, cartCurrencyPrecision);
        const value = ga4Items.reduce((sum, item) => sum + item.price * item.quantity, 0);
        pushGA4EcommerceEvent('remove_from_cart', {
          currency: cartCurrency,
          value,
          items: ga4Items,
        });
      }
    }
    catch (err) {
      console.error(`[useModeCart] Error while removing item ${itemId} from cart:`, err);
      deleteError.value = {
        itemId,
        message: 'cart.details.deleteError',
      };
    }
    finally {
      deletingItemId.value = null;
    }
  }

  function clearDeleteError() {
    deleteError.value = null;
  }

  function formatDateTime(localDateTimeStart: string): string {
    const date = new Date(localDateTimeStart);

    if (Number.isNaN(date.getTime())) {
      console.error(`Invalid date value received: "${localDateTimeStart}"`);
      return '';
    }

    const dayOfWeek = date.toLocaleDateString(currentLocale.value, {
      weekday: 'short',
      timeZone: DEFAULT_TIMEZONE,
    });

    const dayMonth = date.toLocaleDateString(currentLocale.value, {
      day: 'numeric',
      month: 'short',
      timeZone: DEFAULT_TIMEZONE,
    });

    const time = date.toLocaleTimeString(currentLocale.value, {
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
      timeZone: DEFAULT_TIMEZONE,
    });

    return `${dayOfWeek} ${dayMonth} | ${time}`;
  }

  return {
    cartDetails,
    enrichedCartItems,
    deletingItemId,
    deleteError,
    totalPrice,
    hasNoCart,
    isLoadingCart,
    hasOnlyExpiredItems,
    terminalCartState,
    loadCartDetails,
    removeCartItem,
    clearDeleteError,
    formatDateTime,
    getEnrichedUnits,
    isItemExpired,
  };
}
